/**
 * Free moves (`backend/app/story/motion.py`): a hop, a twirl, a somersault, a flight's climb and
 * glide, a balloon's sway, a flower that grows — laid OVER whatever the body is doing (its walk,
 * its clip, the thing it holds), on characters and things alike.
 *
 * A `motion` cue carries keyframes `[u, dx, dy, rot, sx, sy, a]` sampled by the backend from a
 * continuous move; here a Catmull-Rom curve passes through them, so a few keys a second draw a
 * smooth move. Units: `dx`/`dy` in BODY HEIGHTS of the mover where it stands (`dx` forward, the
 * way it faces; `dy` down, negative up), `rot` degrees (positive tilts forward), `sx`/`sy` scale,
 * `a` opacity.
 *
 * Pure, like the runner: `motionAt(entity, t)` reads only the snapshot (the moves in flight and the
 * pose kept from finished ones), so a seek draws what a play-through draws.
 */

export interface MotionPose {
  dx: number
  dy: number
  rot: number
  sx: number
  sy: number
  a: number
}

export const NEUTRAL_POSE: Readonly<MotionPose> = Object.freeze({ dx: 0, dy: 0, rot: 0, sx: 1, sy: 1, a: 1 })

/** A move in flight on a snapshot's actor or prop (`runner.ts` puts it there). */
export interface MotionState {
  id: string
  t0: number
  dur: number
  keys: readonly (readonly number[])[]
  center: boolean
  keep: boolean
  reset: boolean
  shake: readonly [number, number, number] | null
  /** The pose the body kept when this move started (a kept or reset move starts from it). */
  from: MotionPose
}

/** What the renderer needs: the pose now, and whether a turn is about the body's middle. */
export interface MotionNow {
  pose: MotionPose
  center: boolean
  moving: boolean
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v)
const smooth = (u: number): number => {
  const x = clamp(u, 0, 1)
  return x * x * (3 - 2 * x)
}

export function isNeutral(p: MotionPose): boolean {
  return Math.abs(p.dx) < 1e-5 && Math.abs(p.dy) < 1e-5 && Math.abs(p.rot) < 1e-4
    && Math.abs(p.sx - 1) < 1e-5 && Math.abs(p.sy - 1) < 1e-5 && Math.abs(p.a - 1) < 1e-5
}

/** `b` on top of `a`: offsets and turns add, scales and opacity multiply. */
export function compose(a: MotionPose, b: MotionPose): MotionPose {
  return { dx: a.dx + b.dx, dy: a.dy + b.dy, rot: a.rot + b.rot, sx: a.sx * b.sx, sy: a.sy * b.sy, a: a.a * b.a }
}

function lerpPose(a: MotionPose, b: MotionPose, k: number): MotionPose {
  return {
    dx: a.dx + (b.dx - a.dx) * k, dy: a.dy + (b.dy - a.dy) * k, rot: a.rot + (b.rot - a.rot) * k,
    sx: a.sx + (b.sx - a.sx) * k, sy: a.sy + (b.sy - a.sy) * k, a: a.a + (b.a - a.a) * k,
  }
}

function num(v: number | undefined, fallback: number): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback
}

/** The keys' pose at `u` (0..1): a Catmull-Rom curve through them (clamped at the ends). */
export function keyPoseAt(keys: readonly (readonly number[])[], u: number): MotionPose {
  const n = keys.length
  if (n === 0) return { ...NEUTRAL_POSE }
  const uu = clamp(Number.isFinite(u) ? u : 0, 0, 1)
  let i = 0
  while (i < n - 2 && num(keys[i + 1]?.[0], 1) < uu) i += 1
  const k1 = keys[i] as readonly number[]
  const k2 = (keys[i + 1] ?? k1) as readonly number[]
  const k0 = (keys[i - 1] ?? k1) as readonly number[]
  const k3 = (keys[i + 2] ?? k2) as readonly number[]
  const u1 = num(k1[0], 0)
  const u2 = num(k2[0], 1)
  const s = u2 > u1 ? clamp((uu - u1) / (u2 - u1), 0, 1) : 1
  const s2 = s * s
  const s3 = s2 * s
  const at = (j: number, neutral: number): number => {
    const p0 = num(k0[j], neutral)
    const p1 = num(k1[j], neutral)
    const p2 = num(k2[j], neutral)
    const p3 = num(k3[j], neutral)
    return 0.5 * (2 * p1 + (-p0 + p2) * s + (2 * p0 - 5 * p1 + 4 * p2 - p3) * s2 + (-p0 + 3 * p1 - 3 * p2 + p3) * s3)
  }
  return {
    dx: at(1, 0), dy: at(2, 0), rot: at(3, 0),
    sx: at(4, 1), sy: at(5, 1), a: clamp(at(6, 1), 0, 1),
  }
}

/** The fraction of a move done at `t`. */
export function motionU(m: Pick<MotionState, 't0' | 'dur'>, t: number): number {
  return m.dur > 1e-6 ? clamp((t - m.t0) / m.dur, 0, 1) : 1
}

/**
 * The pose a body ends a move with, to keep (`keep`) or lose (`reset`): the next move starts from it.
 */
export function finalPose(m: Pick<MotionState, 'keys' | 'keep' | 'reset' | 'from'>): MotionPose {
  if (m.reset) return { ...NEUTRAL_POSE }
  return m.keep ? compose(m.from, keyPoseAt(m.keys, 1)) : m.from
}

/**
 * The pose of a body with `moves` in flight and the `kept` pose of the finished ones, at story
 * second `t`. A kept or reset move REPLACES the base it started from (it began from that very
 * pose); the other moves ride on top. `still` (reduced motion) drops the trembles.
 */
export function motionAt(
  body: { moves?: readonly MotionState[] | null; pose?: MotionPose | null },
  t: number,
  still = false,
): MotionNow {
  let base: MotionPose = body.pose ?? NEUTRAL_POSE
  let top: MotionPose = NEUTRAL_POSE
  let center = false
  const moves = body.moves ?? []
  for (const m of moves) {
    const u = motionU(m, t)
    const keys = keyPoseAt(m.keys, u)
    if (m.reset) base = compose(lerpPose(m.from, NEUTRAL_POSE, smooth(u)), keys)
    else if (m.keep) base = compose(m.from, keys)
    else top = compose(top, keys)
    if (m.shake && !still) {
      const [amp, hz, deg] = m.shake
      const env = smooth(u / 0.1) * smooth((1 - u) / 0.15)
      const w = 2 * Math.PI * num(hz, 10) * (t - m.t0)
      top = compose(top, { dx: num(amp, 0) * Math.sin(w) * env, dy: 0, rot: num(deg, 0) * Math.sin(w + 1.3) * env, sx: 1, sy: 1, a: 1 })
    }
    center = center || m.center
  }
  const pose = compose(base, top)
  return { pose, center, moving: moves.length > 0 }
}

/** Degrees → radians. */
export const DEG = Math.PI / 180

/**
 * How deep a thing afloat rides (body heights below its feet point, the water's surface): its
 * bottom is under water, cut at the surface by the renderer (`world.ts`).
 */
export const FLOAT_SINK = 0.24
/** Seconds of one swell under a thing afloat. */
export const FLOAT_SWELL_S = 2.6

/**
 * A thing afloat on water (`plate.water`): it rides `FLOAT_SINK` low and the swell lifts, rocks
 * and drifts it a little — the live beach ball lay flat on the sea like a sticker. `phase` (0..1)
 * keeps two floating things out of step; `still` (reduced motion) keeps only the depth.
 */
export function floatPose(t: number, phase: number, still = false): MotionPose {
  if (still) return { dx: 0, dy: FLOAT_SINK, rot: 0, sx: 1, sy: 1, a: 1 }
  const w = 2 * Math.PI * (t / FLOAT_SWELL_S + phase)
  return {
    dx: 0.025 * Math.sin(w * 0.5 + 0.7),
    dy: FLOAT_SINK + 0.04 * Math.sin(w),
    rot: 6 * Math.sin(w * 0.8 + 1.1),
    sx: 1,
    sy: 1,
    a: 1,
  }
}
